import { useEffect, useRef, useState } from 'react';

import { useLocation } from 'js/lib/useRouter';

import { useCoachEpicEnabledState } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { getActiveConversationId, getChatTargetingRule } from 'bundles/common/utils/chatLoaderUtils';
import type { ChatTargetingRule } from 'bundles/common/utils/chatLoaderUtils';
import { getShowGlobalHelpButton } from 'bundles/common/utils/globalHelpButtonUtils';

type ChatWidgetName = 'BoostChat' | 'GlobalHelp' | 'CourseraCoach' | undefined;

const useShouldShowBoostChat = () => {
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [targetingRule, setTargetingRule] = useState<ChatTargetingRule | undefined>(undefined);
  useEffect(() => {
    // Effect code is not run in SSR, which is what we want.  Setting the following using state (rather than a local variables) causes a 2nd render pass in CSR and avoids SSR<>CSR mismatch.
    setConversationId(getActiveConversationId());
    setTargetingRule(getChatTargetingRule(location.href));
  }, []);

  const showBoostChatButton = !!conversationId || !!targetingRule; // If there is an active chat conversation we always show the chat button on all pages because we want the learner to be able to easily resume that conversation.
  return showBoostChatButton;
};

const useShouldShowGlobalHelp = (pathname: string) => {
  const [showGlobalHelpButton, setShowGlobalHelpButton] = useState<boolean>(false);

  useEffect(() => {
    const shouldShowGlobalHelpButton = getShowGlobalHelpButton(pathname).showGlobalHelpButton;
    setShowGlobalHelpButton(shouldShowGlobalHelpButton);
  }, [pathname]);

  return showGlobalHelpButton;
};

export const useChatWidgetSelector = (courseId?: string): ChatWidgetName => {
  const { pathname } = useLocation();

  const { showEmbeddedCoach, isLoading } = useCoachEpicEnabledState({ courseId });
  const shouldShowBoostChat = useShouldShowBoostChat();
  const shouldShowGlobalHelp = useShouldShowGlobalHelp(pathname);

  const [widgetName, setWidgetName] = useState<ChatWidgetName>(undefined);

  useEffect(() => {
    // [COACH-1836] Coach chat can be enabled asynchronously and outside of useCoachEpicEnabledState() checks.
    // So we need to listen for the custom event and set the widget name accordingly.
    const handleCoachChatEnabled = () => {
      setWidgetName('CourseraCoach');
    };

    // custom event dispatched from ExternalCoachLauncher component when Coach chat is enabled.
    document.body.addEventListener('coachChatEnabled', handleCoachChatEnabled);
    return () => {
      document.body.removeEventListener('coachChatEnabled', handleCoachChatEnabled);
    };
  }, []);

  useEffect(() => {
    if (!isLoading) {
      // These widgets are in priority order.
      if (showEmbeddedCoach) {
        setWidgetName('CourseraCoach');
      } else if (shouldShowBoostChat) {
        setWidgetName('BoostChat');
      } else if (shouldShowGlobalHelp) {
        setWidgetName('GlobalHelp');
      }
    }
  }, [shouldShowBoostChat, shouldShowGlobalHelp, showEmbeddedCoach, isLoading]);

  // Track the last returned value to avoid returning undefined during loading states
  const lastValueRef = useRef<ChatWidgetName>(undefined);

  // Update the last value when we have a definitive result
  if (widgetName !== undefined) {
    lastValueRef.current = widgetName;
  }

  // Return the last known value during loading to prevent flickering
  return isLoading ? lastValueRef.current : widgetName;
};

export default useChatWidgetSelector;
